import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, FileText } from 'lucide-react';
import { Report, Customer, User } from '@/types';
import { useAppContext } from '@/contexts/AppContext';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface ExportReportsProps {
  reports: Report[];
  customers: Customer[];
}

const ExportReports: React.FC<ExportReportsProps> = ({ reports, customers }) => {
  const { currentUser } = useAppContext();
  const [open, setOpen] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedSalesperson, setSelectedSalesperson] = useState<string>('all');
  const [users, setUsers] = useState<User[]>([]);

  useEffect(() => {
    const fetchUsers = async () => {
      const { data } = await supabase.from('users').select('*');
      if (data) {
        setUsers(data.map(u => ({
          id: u.id,
          name: u.name,
          email: u.email,
          role: u.role,
          profilePicture: u.profile_picture || ''
        })));
      }
    };
    if (open && currentUser?.role === 'admin') {
      fetchUsers();
    }
  }, [open, currentUser]);

  const getCustomerName = (customerId: string) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.businessName || 'Unknown';
  };

  const filterReports = () => {
    let filtered = [...reports];
    if (startDate) filtered = filtered.filter(r => r.date && r.date >= startDate);
    if (endDate) filtered = filtered.filter(r => r.date && r.date <= endDate);
    if (currentUser?.role === 'admin' && selectedSalesperson !== 'all') {
      filtered = filtered.filter(r => r.salesmanId === selectedSalesperson);
    }
    return filtered;
  };

  const exportToCSV = () => {
    const filtered = filterReports();
    if (filtered.length === 0) {
      toast.error('No reports to export');
      return;
    }
    const headers = ['Business Name', 'Business Type', 'Date', 'Time', 'Director', 'Product', 'Phone', 'Competitor', 'Goal', 'Salesperson'];
    const rows = filtered.map(r => [
      getCustomerName(r.customerId),
      r.businessType || '',
      r.date || '',
      r.time || '',
      r.director || '',
      Array.isArray(r.product) ? r.product.join('; ') : r.product,
      r.phone || '',
      r.competitor || '',
      r.goal || '',
      r.salespersonName || ''
    ]);
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reports_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('CSV exported successfully');
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Download className="h-4 w-4" />
          Export
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Export Reports</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </div>
          <div>
            <Label>End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </div>
          {currentUser?.role === 'admin' && (
            <div>
              <Label>Salesperson</Label>
              <Select value={selectedSalesperson} onValueChange={setSelectedSalesperson}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Salespeople</SelectItem>
                  {users.filter(u => u.role === 'salesperson').map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={exportToCSV} className="w-full gap-2">
            <FileText className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExportReports;
